import type {
  CarrierOkLookupData,
  InviteCarrierData,
  InviteCarrierRequest,
} from "api-client";
import { createCarriersService, rejectMessage } from "api-client";
import type { InviteCarrierLookupResult } from "design-components";
import { OPERATOR_SCOPED_ID } from "../constants/operator.constants";
import { mapInviteCarrierDataToListItem } from "../utils/invite-carrier-list-item.mapper";
import { bookingsService } from "./bookings.service";

const baseUrl =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() || "";

export const carriersService = createCarriersService(baseUrl);

/** Shared invite failure copy (`typescript:S1192`). */
export const INVITE_CARRIER_FAILED_MESSAGE = "Failed to invite carrier.";

const LOOKUP_CARRIER_NOT_FOUND_MESSAGE =
  "No carrier found for this DOT number. Turn off Federal DOT to enter details manually.";

export interface InviteCarrierFormRequest {
  customerId?: number;
  customerName?: string;
  carrierName: string;
  contactName: string;
  email: string;
  phone?: string;
  scac?: string;
  stateId?: string;
  businessAddress?: string;
  dotNumber: string;
  ifCarrierHasFederalDot: boolean;
  dotStatus?: string;
  riskScore?: string;
  safetyScore?: number;
}

export interface InviteCarrierOperatorResult {
  listItem: ReturnType<typeof mapInviteCarrierDataToListItem>;
  data: InviteCarrierData;
}

function isApiSuccessStatus(statusCode: number): boolean {
  return statusCode === 200 || statusCode === 201;
}

function mapDotStatusToWire(body: InviteCarrierFormRequest): boolean {
  if (!body.ifCarrierHasFederalDot) return false;
  const label = (body.dotStatus ?? "").trim().toLowerCase();
  if (!label) return true;
  return label === "active" || label === "true" || label === "1";
}

function buildWireInviteRequest(body: InviteCarrierFormRequest): InviteCarrierRequest {
  const wire: InviteCarrierRequest = {
    carrierName: body.carrierName.trim(),
    contactName: body.contactName.trim(),
    email: body.email.trim(),
    scacCode: body.scac?.trim() || undefined,
    businessAddress: body.businessAddress?.trim() || undefined,
    stateProvinceId: body.stateId?.trim() || undefined,
    riskScore: body.riskScore?.trim() || undefined,
    safetyScore: body.safetyScore,
  };

  if (body.customerId != null && body.customerId > 0) {
    wire.customerId = body.customerId;
  }

  if (body.ifCarrierHasFederalDot) {
    wire.dotNumber = body.dotNumber.trim();
    wire.dotStatus = mapDotStatusToWire(body);
  }

  const phone = body.phone?.trim();
  if (phone) {
    wire.phone = phone;
  }

  return wire;
}

function mapLookupData(data: CarrierOkLookupData): InviteCarrierLookupResult {
  return {
    carrierName: data.carrierName,
    contactName: data.contactName,
    email: data.email,
    businessAddress: data.businessAddress,
    dotStatus: data.dotStatus,
    riskScore: data.riskScore,
    safetyScore: String(data.safetyScore),
  };
}

/** FR-AV166-004 / FR-AV166-014 — Carrier OK lookup for Add Carrier. */
export async function lookupCarrierOkForAdd(
  dotNumber: string,
): Promise<InviteCarrierLookupResult> {
  const trimmed = dotNumber.trim();
  if (!trimmed) {
    throw new Error("Enter a DOT number.");
  }

  let res;
  try {
    res = await bookingsService.lookupCarrierByDot(trimmed);
  } catch (e: unknown) {
    throw new Error(rejectMessage(e, LOOKUP_CARRIER_NOT_FOUND_MESSAGE));
  }

  if (isApiSuccessStatus(res.statusCode) && res.data?.carrierName?.trim()) {
    return mapLookupData(res.data);
  }
  throw new Error(res.message?.trim() || LOOKUP_CARRIER_NOT_FOUND_MESSAGE);
}

/** FR-AV166-008 / FR-AV166-014 — Invite carrier from Operator Add Carrier. */
export async function inviteCarrierOperator(
  body: InviteCarrierFormRequest,
): Promise<InviteCarrierOperatorResult> {
  const wire = buildWireInviteRequest(body);

  let res;
  try {
    res = await carriersService.inviteCarrier(OPERATOR_SCOPED_ID, wire);
  } catch (e: unknown) {
    throw new Error(rejectMessage(e, INVITE_CARRIER_FAILED_MESSAGE));
  }

  if (res.statusCode === 409) {
    throw new Error(
      res.message?.trim() || "A carrier with this DOT number already exists.",
    );
  }
  if (!isApiSuccessStatus(res.statusCode) || !res.data) {
    throw new Error(res.message?.trim() || INVITE_CARRIER_FAILED_MESSAGE);
  }
  const carrierId = res.data.carrierId;
  if (!carrierId || !Number.isFinite(carrierId) || carrierId <= 0) {
    throw new Error(res.message?.trim() || INVITE_CARRIER_FAILED_MESSAGE);
  }
  return {
    data: { ...res.data, carrierId, status: res.data.status ?? "InviteSent" },
    listItem: mapInviteCarrierDataToListItem(
      res.data,
      carrierId,
      body.customerName,
    ),
  };
}
